import { JSONSchema7 } from 'json-schema';
import React, { memo, useState } from 'react';
import { Handle, NodeProps, Position, useStore } from 'reactflow';

import { Close } from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Drawer, IconButton, Typography } from '@mui/material';

import { JSONSchema } from '../../json-schema-viewer';
import { processSourceKeys } from '../../json-schema-viewer/components';
import useDeleteNode from '../hooks/useDeleteNode';
import useUpdateNodeData from '../hooks/useUpdateNodeData';
import EvaluatorNodeConfig from '../nodes-config/EvaluatorNodeConfig';
import TransformerNodeConfig from '../nodes-config/TransformerNodeConfig';
import styles from './NodeTypes.module.css';


const isConfiguredNode = (nodeConfig: any): boolean => {
    const value: boolean = nodeConfig &&
        "blockName" in nodeConfig &&
        "commitId" in nodeConfig &&
        ("schema" in nodeConfig && processSourceKeys(nodeConfig?.schema.properties, 'validate')) &&
        "activityConfig" in nodeConfig;

    return value;
}

// Define the properties and state types
export interface BaseNodeProps extends NodeProps {
    name: string;
    description?: string | null;
    icon?: React.ElementType | null;
    color?: string;
    supportedSourceTypes: string[];
    onNodeDeleteClick?: (id: NodeProps['id']) => void;
    onNodeClick?: (id: NodeProps['id']) => void;
    nodeConfig?: any;
    nodeConfigSchema?: JSONSchema7;
    displayText?: string;
}

const BaseNode: React.FC<BaseNodeProps> = (props) => {

    const IconComponent = props.icon ? <props.icon style={{ color: props.color }} /> : null;
    const handleStyles = {
        background: props.color,
    };

    const handleDelete = useDeleteNode(props.id);
    const updateNodeData = useUpdateNodeData();
    const updateFileStates = useStore((state: any) => state.updateFileStates);

    const [openNodeConfigDrawer, setOpenNodeConfigDrawer] = useState<boolean>(false);
    const [drawerContent, setDrawerContent] = useState<BaseNodeProps | null>(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);


    const handleNodeClick = (event: React.MouseEvent) => {
        if (props.onNodeClick) {
            props.onNodeClick(props.id);
        }
        console.log("props: ", props)
        setDrawerContent(props);
        setOpenNodeConfigDrawer(true);
    };

    const handleNodeConfigSubmit = (
        nodeConfigData: any,
        root_schema: JSONSchema7,
        outputSchema?: JSONSchema,
        extraData?: object,
    ) => {
        const updatedData: any = {
            nodeConfig: nodeConfigData,
        }

        if (outputSchema) {
            updatedData["outputSchema"] = outputSchema
        }

        if (extraData) {
            updatedData["meta_data"] = extraData
        }

        console.log("props id: ", props.id)
        console.log("final saving config data: ", updatedData)

        updateNodeData(props.id, updatedData);
        updateFileStates && updateFileStates(root_schema);
    }


    return (
        <>
            <Box className={styles.node} onClick={handleNodeClick}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {IconComponent}
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">
                            {props?.nodeConfig ? props.nodeConfig.blockName : props.name}
                        </Typography>
                        {props.displayText && (
                            <Typography variant="caption">{props.displayText}</Typography>
                        )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {isConfiguredNode(props.nodeConfig) ? (
                            <CheckIcon style={{ color: 'green' }}></CheckIcon>
                        ) : (
                            <ErrorIcon style={{ color: 'darkorange' }}></ErrorIcon>
                        )}
                        <IconButton onClick={(e) => {
                            e.stopPropagation();
                            setOpenDeleteDialog(true)
                        }}>
                            <CancelIcon style={{ color: 'rgba(0, 0, 0, 0.3)' }}></CancelIcon>
                        </IconButton>
                    </div>
                </div>

                <Divider className='divider-margin-top-bottom' />

                {props?.nodeConfig && <Box display='flex' justifyContent='center' gap={1}>
                    {/* <Typography variant="caption">{props.nodeConfig.blockName}</Typography>
                    <Divider orientation='vertical' flexItem sx={{ borderColor: '#bbb' }} /> */}
                    <Typography variant="caption">Commit: {props.nodeConfig.commitId.substring(0, 10)}</Typography>
                </Box>}

                {!props?.nodeConfig && props.description && (
                    <Typography variant="caption" className={styles.textOverflow}>
                        {props.description}
                    </Typography>
                )}
                <Handle
                    className="handle"
                    type="target"
                    position={Position.Top}
                    isConnectable={false}
                    style={handleStyles}
                />
                <Handle
                    className="handle"
                    type="source"
                    position={Position.Bottom}
                    isConnectable={false}
                    style={handleStyles}
                />
            </Box>
            <Drawer
                anchor="right"
                open={openNodeConfigDrawer}
                onClose={() => setOpenNodeConfigDrawer(false)}
                ModalProps={{
                    BackdropProps: {
                        style: {
                            backgroundColor: 'transparent',
                        },
                    },
                }}
                sx={{
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: "50%",
                    },
                }}
            >
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: "8px 16px" }}>
                        <Box>
                            <Typography variant="h6">{drawerContent?.name}</Typography>
                            <Typography variant="subtitle2">{drawerContent?.description}</Typography>
                        </Box>

                        <IconButton edge="end" onClick={() => setOpenNodeConfigDrawer(false)} aria-label="close">
                            <CancelIcon />
                        </IconButton>
                    </Box>

                    <Divider sx={{}} />

                    <Box sx={{ padding: 2 }}>
                        {drawerContent && (
                            drawerContent.type === 'transformerNode' &&
                            <TransformerNodeConfig
                                drawerContent={drawerContent}
                                setOpenNodeConfigDrawer={setOpenNodeConfigDrawer}
                                handleFormSubmit={handleNodeConfigSubmit}
                            />
                        )}

                        {drawerContent && (
                            drawerContent.type === 'evaluatorNode' &&
                            <EvaluatorNodeConfig
                                drawerContent={drawerContent}
                                setOpenNodeConfigDrawer={setOpenNodeConfigDrawer}
                                handleFormSubmit={handleNodeConfigSubmit}
                            />
                        )}
                    </Box>
                </Box>
            </Drawer>

            <Dialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
                // component='form'
                // onSubmit={handleSubmit(handleBasicInfoSubmit)}
                aria-labelledby="delete-dialog"
                sx={{
                    '& .MuiDialog-paperWidthSm': {
                        width: '650px',
                        maxWidth: '650px',
                        height: '220px'
                    },
                }}
            >
                <DialogTitle id="customized-dialog-title"
                    sx={{
                        m: 0,
                        pt: 1.5,
                        pb: 1,
                        pr: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Box>
                        <Typography variant='h6' component='p'>DELETE</Typography>
                    </Box>

                    <IconButton
                        aria-label="close"
                        type='button'
                        onClick={() => setOpenDeleteDialog(false)}
                        sx={{ width: 45, height: 45 }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <Divider />

                <DialogContent>
                    <DialogContentText>
                        Deleting this node will delete configuration for selected node and all it's children!
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    {props.type !== 'evaluatorNode' &&
                        <Button
                            type='button'
                            variant="outlined"
                            color='warning'
                            onClick={() => {
                                handleDelete('configure');
                                setOpenDeleteDialog(false);
                            }}
                            sx={{ width: 140 }}
                        >
                            re-configure
                        </Button>
                    }

                    <Button
                        type='submit'
                        variant="contained"
                        color='error'
                        onClick={() => handleDelete('delete')}
                        sx={{ width: 140 }}
                        disableElevation
                    >
                        delete all
                    </Button>
                </DialogActions>
            </Dialog>
        </>

    );
}

export default memo(BaseNode);
